// i18n core — t(), useTranslation(), type-safe key resolution.
//
// Backed by useSettingsStore.language (persisted via PATCH /api/settings).
// Default locale is 'en'. Components call t('sidebar.empty.title') and
// get the string for the active locale with {param} interpolation.

import { useCallback } from 'react';
import en, { type Translations } from './en';
import es from './es';
import { useSettingsStore } from '../stores/settings.store';

// ── Locale type ──────────────────────────────────────────────────────

export type Locale = 'en' | 'es';

// ── Compile-time key parity check ────────────────────────────────────
// If es.ts is missing a key that en.ts has (or vice versa), this line
// produces a type error. The `as const` + Translations type on es.ts
// already enforces structural identity; this re-export makes it
// explicit for consumers.

const locales: Record<Locale, Translations> = { en, es };

// ── Flatten nested keys into dot-separated paths ─────────────────────

type Join<K, P> = K extends string
  ? P extends string
    ? `${K}.${P}`
    : never
  : never;

type Leaves<T> = T extends object
  ? { [K in keyof T]: T[K] extends object ? Join<K, Leaves<T[K]>> : K }[keyof T]
  : never;

export type TKey = Leaves<Translations>;

// ── Core resolver ────────────────────────────────────────────────────

function resolve(obj: Translations, path: string): string {
  // Sprint runner-resilience-after-missing-credential §2.5 — defensive
  // early return for non-string `path`. A missing translation key is a
  // bug (caller passed `undefined`, typically because a `Record<…>`
  // lookup returned undefined for an enum value the frontend doesn't
  // know about yet — exactly the failure mode that crashed the whole
  // Settings page on 2026-05-17 when the backend emitted the new
  // `missing_credential` reason). Returning `'[missing]'` is
  // intentional: visible enough to surface the bug in development;
  // harmless enough that the surrounding UI keeps rendering.
  if (typeof path !== 'string') return '[missing]';
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return path;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : path;
}

function interpolate(
  template: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const val = params[key];
    return val !== undefined ? String(val) : `{${key}}`;
  });
}

// ── Standalone t() for non-React contexts ────────────────────────────

let activeLocale: Locale = 'en';

export function setActiveLocale(locale: Locale): void {
  activeLocale = locale;
}

export function t(key: TKey, params?: Record<string, string | number>): string {
  const raw = resolve(locales[activeLocale], key);
  return interpolate(raw, params);
}

// ── React hook ───────────────────────────────────────────────────────

export function useTranslation() {
  const language = useSettingsStore((s) => s.language);
  const setLanguage = useSettingsStore((s) => s.setLanguage);

  // Keep the module-level locale in sync for non-React callers
  // (e.g. lib/permissions/humanize.ts).
  if (language !== activeLocale) {
    activeLocale = language;
  }

  const translate = useCallback(
    (key: TKey, params?: Record<string, string | number>): string => {
      const raw = resolve(locales[language], key);
      return interpolate(raw, params);
    },
    [language],
  );

  return {
    t: translate,
    locale: language,
    setLocale: setLanguage,
  };
}

// Re-export for convenience
export type { Translations } from './en';
